import React, {useEffect, useState} from "react";
import {Box, Button, Column, Columns} from "@allxsmith/bestax-bulma";
import Nav from "../Nav/Nav";
import LabelPrinterModal from "./LabelPrinterModal";
import {HELPER_DOWNLOAD, getSitePrinter, helperStatus} from "./labelPrinter";

// Parts > Label Printer: install the helper on this PC, then set up the site's Zebra.
export default function LabelPrinterPage() {
    const [helper, setHelper] = useState(undefined);   // undefined = checking, null = not running
    const [printer, setPrinter] = useState(null);
    const [setupOpen, setSetupOpen] = useState(false);

    const check = () => {
        setHelper(undefined);
        helperStatus().then(setHelper);
        getSitePrinter().then(setPrinter).catch(() => setPrinter(null));
    };
    useEffect(check, []);
    useEffect(() => { if (!setupOpen) check(); }, [setupOpen]);

    document.title = "Label Printer";
    const ready = helper && printer && printer.ip;

    return (
        <div>
            <Nav/>
            <br/><br/>
            <Box>
                <h1 className="title">Label printer for FedEx labels</h1>
                <p>FedEx labels print on the Zebra label printer on the network. Each PC that prints labels needs
                    the small <strong>ACS Label Printer Helper</strong> installed once - a web page can't reach a
                    network printer on its own. The printer itself is set up once for the whole site.</p>

                <Columns style={{marginTop: "12px"}}>
                    <Column>
                        <div className={"notification " + (helper ? "is-success" : helper === null ? "is-danger" : "is-light") + " is-light"}>
                            <strong>This PC: </strong>
                            {helper === undefined ? "checking..." :
                                helper ? "helper installed and running (v" + helper.version + ")" : "helper not installed (or not running)"}
                        </div>
                    </Column>
                    <Column>
                        <div className={"notification " + (printer && printer.ip ? "is-success" : "is-warning") + " is-light"}>
                            <strong>Site printer: </strong>
                            {printer && printer.ip ? printer.name + " (" + printer.ip + ")" : "not set up yet"}
                        </div>
                    </Column>
                </Columns>
                <div className="buttons">
                    <Button color="info" onClick={check}>Check again</Button>
                    <Button color="primary" disabled={!helper} onClick={() => setSetupOpen(true)}>Printer setup</Button>
                </div>
                {ready ? <p className="has-text-success"><strong>This PC is ready to print FedEx labels.</strong></p> : null}
            </Box>

            <Box>
                <h2 className="title is-4">1. Install the helper on this PC</h2>
                <p className="has-text-grey" style={{marginBottom: "10px"}}>
                    Takes about a minute. You need to be able to approve administrator changes on the PC - if you
                    can't, ask ACS to install it remotely.
                </p>
                <ol style={{marginLeft: "22px", lineHeight: "1.9"}}>
                    <li>
                        <a className="button is-primary is-small" href={HELPER_DOWNLOAD} download>Download the helper</a>
                        {" "}(ACSLabelHelper.zip)
                    </li>
                    <li>Open your <strong>Downloads</strong> folder, right-click <strong>ACSLabelHelper.zip</strong> and
                        choose <strong>Extract All</strong>, then <strong>Extract</strong>.</li>
                    <li>In the folder that opens, double-click <strong>install.cmd</strong>.
                        <ul style={{listStyle: "disc", marginLeft: "22px"}}>
                            <li>If a blue box says <em>"Windows protected your PC"</em>, click <strong>More info</strong>, then <strong>Run anyway</strong>.</li>
                            <li>If a box says <em>"Open File - Security Warning"</em>, click <strong>Run</strong>.</li>
                        </ul>
                    </li>
                    <li>Click <strong>Yes</strong> when Windows asks <em>"Do you want to allow this app to make changes to your device?"</em></li>
                    <li>Wait for <strong>Installed and running</strong>, then press <strong>Enter</strong> to close the window.</li>
                    <li><strong>Close every Chrome/Edge window</strong> and open it again, come back to this page and
                        click <strong>Check again</strong> - "This PC" should turn green.</li>
                </ol>
            </Box>

            <Box>
                <h2 className="title is-4">2. Set up the printer (once for the whole site)</h2>
                <p className="has-text-grey" style={{marginBottom: "10px"}}>
                    Only needed the first time, or if the printer is replaced or moves. Every PC then uses the same printer.
                </p>
                <ol style={{marginLeft: "22px", lineHeight: "1.9"}}>
                    <li>Make sure the Zebra is switched on and connected to the network.</li>
                    <li>Click <strong>Printer setup</strong> above, then <strong>Auto-scan for Zebra printers</strong> (takes up to a minute).</li>
                    <li>Click <strong>Use this printer</strong> next to the Zebra.</li>
                    <li>Click <strong>Print test label</strong> - a label saying <em>"ACS label test"</em> should come out.</li>
                </ol>
            </Box>

            <Box>
                <h2 className="title is-4">If something isn't working</h2>
                <ul style={{listStyle: "disc", marginLeft: "22px", lineHeight: "1.9"}}>
                    <li><strong>"This PC" still says not installed</strong> - sign out of Windows and back in (the helper
                        starts at logon), then reopen the browser and click Check again.</li>
                    <li><strong>The browser asks to "access other apps and devices on your network"</strong> - click <strong>Allow</strong>.</li>
                    <li><strong>Auto-scan finds no printer</strong> - check the Zebra is on and its network cable is in. If
                        you know its IP address (it's on the printer's configuration label), type it into Printer setup instead.</li>
                    <li><strong>The test label doesn't print</strong> - check the printer has labels and isn't showing an
                        error light, then try again. Labels still download as a .zpl file if printing fails, so nothing is lost.</li>
                    <li><strong>Still stuck</strong> - contact ACS support.</li>
                </ul>
                <p style={{marginTop: "10px"}} className="has-text-grey">
                    To remove the helper from a PC, double-click <strong>uninstall.cmd</strong> in the same folder.
                </p>
            </Box>

            <LabelPrinterModal open={setupOpen} setOpen={setSetupOpen}/>
        </div>
    );
}
